import React from 'react';
import { RowModel, flexRender } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { NavigationConfig } from '@/shared/ui/AppTable/ui/AppTable/AppTable';
import { IntakeGroupRow } from '../IntakeGroupRow';
import styles from './IntakesTableBody.module.scss';

type IntakeRow = {
  id: number;
  groups?: Array<{ id: number; number: number; studentCount: number; title?: string }>;
};

interface IntakesTableBodyProps<TData> {
  rows: RowModel<TData>['rows'];
  navigationConfig?: NavigationConfig<TData>[];
  onRowClick?: (row: TData) => void;
  expandedIntakes: Set<number>;
  courseId: number;
}

export const IntakesTableBody = <T extends IntakeRow>({
  rows,
  navigationConfig,
  onRowClick,
  expandedIntakes,
  courseId,
}: IntakesTableBodyProps<T>) => {
  const getNavigationConfig = (columnId: string) =>
    navigationConfig?.find((config) => config.columnId === columnId);

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <tbody>
      {rows.map((row) => {
        const intake = row.original as IntakeRow;
        const isExpanded = expandedIntakes.has(intake.id);
        const hasGroups = intake.groups && intake.groups.length > 0;

        return (
          <React.Fragment key={row.id}>
            <tr
              className={`${styles.row} ${onRowClick ? styles.clickable : ''}`}
              onClick={() => handleRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => {
                const navConfig = getNavigationConfig(cell.column.id);
                const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());

                return (
                  <td key={cell.id} className={styles.cell}>
                    {navConfig ? (
                      <Link to={navConfig.getUrl(row.original)} className={styles.navigationLink}>
                        {cellContent}
                      </Link>
                    ) : (
                      cellContent
                    )}
                  </td>
                );
              })}
            </tr>
            {isExpanded && hasGroups && intake.groups && (
              <>
                {intake.groups.map((group) => (
                  <IntakeGroupRow key={`group-${intake.id}-${group.id}`} group={group} intakeId={intake.id} courseId={courseId} />
                ))}
              </>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
};
